import React from 'react';
import { Check } from 'lucide-react';
import { useTheme } from '../theme/ThemeProvider';
import { previewInk, THEMES } from '../theme/tokens';

/**
 * Theme picker for the Settings modal: one card per registered theme, rendered
 * straight from THEMES so adding a theme stays a one-line change in tokens.js.
 *
 * Each card is a miniature of the app painted in the theme it offers — you pick
 * by recognising the look, not by reading the name. The colours a card paints
 * itself with all come from the registry (swatch + previewInk), never from the
 * theme that happens to be active.
 */

export default function ThemeSection() {
  const { resolvedTheme, setTheme } = useTheme();
  // A theme id left over in localStorage from an older build can outlive its
  // registry entry; fall back to the first theme so no card looks selected
  // rather than pretending the stale id is one of these.
  const activeId = THEMES.some((t) => t.id === resolvedTheme) ? resolvedTheme : THEMES[0]?.id;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {THEMES.map(({ id, label, swatch: [surface, accent] }) => {
        const active = id === activeId;
        const ink = previewInk(id);
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            aria-label={`Use the ${label} theme`}
            onClick={() => setTheme(id)}
            className={`relative flex flex-col gap-2 rounded-xl border p-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
              active ? 'border-line ring-2 ring-brand' : 'border-line hover:border-line-strong'
            }`}
            style={{
              transitionDuration: 'var(--d-fast)',
              transitionTimingFunction: 'var(--ease-out)',
            }}
          >
            {/* Mock of the app in this theme: its surface, one accent bar, two
                text bars. Spans, not divs — a button only takes phrasing content. */}
            <span
              aria-hidden="true"
              className="block overflow-hidden rounded-lg px-2.5 py-3"
              style={{ background: surface, boxShadow: `inset 0 0 0 1px ${ink.edge}` }}
            >
              <span className="flex flex-col gap-1.5">
                <span className="block h-1.5 w-1/3 rounded-full" style={{ background: accent }} />
                <span className="block h-1.5 w-full rounded-full" style={{ background: ink.bar }} />
                <span className="block h-1.5 w-2/3 rounded-full" style={{ background: ink.bar }} />
              </span>
            </span>

            {active && (
              <span
                className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-brand text-brand-fg"
                style={{ boxShadow: 'var(--shadow-soft)' }}
              >
                <Check size={12} strokeWidth={3} aria-hidden="true" />
              </span>
            )}

            <span className={`px-0.5 text-xs font-medium ${active ? 'text-fg' : 'text-fg-secondary'}`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Declared empty on purpose: this section takes no props at all — the active
// theme comes from useTheme() and the card list from the THEMES registry — so
// there is nothing to validate and a parent can drop it in anywhere. (No
// prop-types import for the same reason; an unused one is a build warning.)
ThemeSection.propTypes = {};
